import { DollarSign, ExternalLink, FileText, Shield } from 'lucide-react';
import { getLearningCenterBaseUrl } from '../constants/learning-center';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from './ui/dialog';

interface HowItWorksModalProps {
  isOpen: boolean;
  onClose: () => void;
  isFirstTimeUser?: boolean;
  onNavigateToSettings?: () => void;
  onSetupEncryption?: () => void;
  showBillingPrompt?: boolean;
  showEncryptionPrompt?: boolean;
}

export function HowItWorksModal({
  isOpen,
  onClose,
  isFirstTimeUser = false,
  onNavigateToSettings,
  onSetupEncryption,
  showBillingPrompt = false,
  showEncryptionPrompt = false,
}: HowItWorksModalProps) {
  const handleBillingSetup = () => {
    if (onNavigateToSettings) {
      onNavigateToSettings();
      onClose();
    }
  };

  const handleEncryptionSetup = () => {
    if (onSetupEncryption) {
      onClose();
      // Delay encryption modal to let onboarding modal finish closing
      setTimeout(() => onSetupEncryption(), 200);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto bg-white">
        <DialogHeader>
          <DialogTitle className="text-xl font-bold text-gray-900">
            {isFirstTimeUser ? 'Welcome to ExpertMD!' : 'How ExpertMD Works'}
          </DialogTitle>
          <DialogDescription className="text-sm text-gray-600">
            {isFirstTimeUser
              ? "Let's get you started with your expert witness practice"
              : 'Quick guide to managing expert witness cases'}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 mt-4">
          {/* Video Section */}
          <div className="w-full overflow-hidden rounded-lg bg-black">
            <iframe
              allow="encrypted-media; picture-in-picture; fullscreen"
              allowFullScreen
              className="w-full border-0"
              style={{
                aspectRatio: '16 / 9',
                minHeight: '400px',
              }}
              src="https://supercut.ai/embed/8862aa5b-aeaf-420b-8ec4-b/oU01zmAHeYuWzaW9Nom9U8?embed=full"
              title="ExpertMD Tutorial"
            />
          </div>

          {/* Quick Overview */}
          <div className="bg-[#EAE0CB]/40 border border-[#3A2718]/15 rounded-lg p-3">
            <p className="text-sm text-gray-700">
              ExpertMD streamlines your workflow from case screening to payment.
            </p>
          </div>

          {/* Setup Checklist */}
          <div className="bg-gradient-to-br from-green-50 to-emerald-50 border border-green-200 rounded-lg p-4">
            <h3 className="text-sm font-semibold text-gray-900 mb-3">Essential Setup Steps</h3>
            <div className="space-y-3">
              {/* Encryption Passcode */}
              <div className="flex items-start gap-3">
                <div className="p-1.5 bg-[#EAE0CB] rounded-full flex-shrink-0">
                  <Shield className="h-4 w-4 text-[#A66B32]" />
                </div>
                <div className="flex-1">
                  <p className="text-sm font-medium text-gray-900">Create encryption passcode</p>
                  <p className="text-xs text-gray-600 mt-0.5">
                    Ensure your case work is end-to-end encrypted for maximum security
                  </p>
                  <p className="text-xs text-[#A66B32] font-medium mt-1">
                    → Go to Settings → Security
                  </p>
                </div>
              </div>

              {/* Billing Setup */}
              <div className="flex items-start gap-3">
                <div className="p-1.5 bg-amber-100 rounded-full flex-shrink-0">
                  <DollarSign className="h-4 w-4 text-amber-600" />
                </div>
                <div className="flex-1">
                  <p className="text-sm font-medium text-gray-900">Add billing information</p>
                  <p className="text-xs text-gray-600 mt-0.5">
                    Connect your payment account so you can get paid by your clients
                  </p>
                  <p className="text-xs text-amber-600 font-medium mt-1">
                    → Go to Settings → Billing
                  </p>
                </div>
              </div>

              {/* Invoice Services */}
              <div className="flex items-start gap-3">
                <div className="p-1.5 bg-purple-100 rounded-full flex-shrink-0">
                  <FileText className="h-4 w-4 text-purple-600" />
                </div>
                <div className="flex-1">
                  <p className="text-sm font-medium text-gray-900">Add invoice services</p>
                  <p className="text-xs text-gray-600 mt-0.5">
                    Configure your service rates to streamline billing
                  </p>
                  <p className="text-xs text-purple-600 font-medium mt-1">
                    → Go to Invoices → Manage Services
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Setup Prompts for First-Time Users */}
          {isFirstTimeUser && (showBillingPrompt || showEncryptionPrompt) && (
            <div className="space-y-3 border-t border-gray-200 pt-4">
              <h3 className="text-sm font-semibold text-gray-900">Next Steps</h3>

              {/* Billing Setup Prompt */}
              {showBillingPrompt && (
                <div className="flex items-start gap-3 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                  <DollarSign className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <h4 className="text-sm font-medium text-gray-900 mb-1">Set Up Billing</h4>
                    <p className="text-xs text-gray-600 mb-2">
                      Connect your Stripe account to receive payments from clients.
                    </p>
                    <Button
                      size="sm"
                      onClick={handleBillingSetup}
                      className="bg-amber-600 hover:bg-amber-700 text-white"
                    >
                      Go to Billing Settings
                    </Button>
                  </div>
                </div>
              )}

              {/* Encryption Setup Prompt */}
              {showEncryptionPrompt && (
                <div className="flex items-start gap-3 p-3 bg-[#EAE0CB]/40 border border-[#3A2718]/15 rounded-lg">
                  <Shield className="h-5 w-5 text-[#A66B32] flex-shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <h4 className="text-sm font-medium text-gray-900 mb-1">
                      Enable End-to-End Encryption
                    </h4>
                    <p className="text-xs text-gray-600 mb-2">
                      Protect your sensitive case data with a secure encryption passcode.
                    </p>
                    <Button
                      size="sm"
                      onClick={handleEncryptionSetup}
                      className="bg-[#3A2718] hover:bg-[#241610] text-white"
                    >
                      Setup Encryption Passcode
                    </Button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Learning Center Link */}
          <div className="border-t border-gray-200 pt-4">
            <a
              href={getLearningCenterBaseUrl()}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 text-sm text-[#A66B32] hover:text-[#3A2718] transition-colors"
            >
              Visit the Learning Center for detailed guides
              <ExternalLink className="h-3.5 w-3.5" />
            </a>
          </div>

          {/* Close Button */}
          <div className="flex justify-end">
            <Button onClick={onClose} className="bg-[#3A2718] hover:bg-[#241610] text-white">
              {isFirstTimeUser ? "Let's Get Started!" : 'Got It!'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
